import { useMemo } from 'react';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import ParentHTTPService from 'services/HTTPService/parent/ParentHTTPService';
import { useSearchParamsBasedPagination } from 'hooks/useSearchParamsBasedPagination/useSearchParamsBasedPagination';
import { parsePaginationDataToTablePaginationData } from 'utils/parsers/table/parsePaginationDataToTablePaginationData';
import { ChildTransactionTable } from 'components/organisms';
import {
    ChildId,
    ChildPayment,
    ChildPaymentMethod,
    ChildPaymentSource,
    ChildPaymentType,
    ChildTransactionListItem,
    ChildTransactionPayment,
    InstitutionId,
    PaymentTypes,
    TransferTypes,
} from 'types';

type ParentTransactionRecord = ChildPayment & Partial<ChildTransactionPayment>;

const parseTransactionType = (
    payment: ParentTransactionRecord,
): ChildTransactionListItem['type'] => {
    if (payment.type) {
        return payment.type;
    }

    return payment.transactionType === TransferTypes.REFUND
        ? ChildPaymentType.COST
        : ChildPaymentType.PAYMENT;
};

const parsePaymentMethod = (
    payment: ParentTransactionRecord,
): ChildTransactionListItem['paymentMethod'] => {
    if (payment.paymentMethod) {
        return payment.paymentMethod;
    }

    switch (payment.paymentFormType) {
        case PaymentTypes.CASH:
            return ChildPaymentMethod.CASH;
        case PaymentTypes.PAYU:
            return ChildPaymentMethod.PAYU;
        case PaymentTypes.WIRE_TRANSFER:
            return ChildPaymentMethod.BANK_TRANSFER;
        default:
            return ChildPaymentMethod.OTHER;
    }
};

export const ParentTransactions = () => {
    const { currentPage, currentPageSize, handlePaginationValuesChange } =
        useSearchParamsBasedPagination();

    const {
        data: childPaymentData,
        isRefetching: isChildPaymentRefetching,
        isInitialLoading: isChildPaymentInitialLoading,
        isError: isChildPaymentError,
    } = useAppQuery(
        'CHILD_SETTLEMENT_PAYMENT',
        [currentPage, currentPageSize],
        () =>
            ParentHTTPService.settlements.getChildTransactions({
                paginationParams: { size: currentPageSize, page: currentPage },
            }),
        {},
    );

    const mappedData = useMemo<ChildTransactionListItem[] | undefined>(() => {
        if (!childPaymentData?.content) {
            return undefined;
        }

        return childPaymentData.content.map(
            (payment: ParentTransactionRecord): ChildTransactionListItem => ({
                id: payment.id,
                childId: payment.childId || ('' as ChildId),
                institutionId: payment.institutionId || ('' as InstitutionId),
                dateTime: payment.dateTime || payment.date,
                amount: payment.amount,
                category: payment.category || payment.balanceTargetType,
                relatedTransactionId: payment.id,
                description: payment.description || null,
                type: parseTransactionType(payment),
                source: payment.source || ChildPaymentSource.SETTLEMENT,
                paymentMethod: parsePaymentMethod(payment),
            }),
        );
    }, [childPaymentData]);

    return (
        <ChildTransactionTable
            isError={isChildPaymentError}
            isDataInitialLoading={isChildPaymentInitialLoading}
            isDataRefetching={isChildPaymentRefetching}
            isHiddenAddButton
            pagination={{
                ...parsePaginationDataToTablePaginationData({
                    currentPage,
                    currentPageSize,
                }),
                total: childPaymentData?.totalElements || 0,
                onChange: handlePaginationValuesChange,
            }}
            dataSource={mappedData}
        />
    );
};
